// src/errors.ts
var LootApiError = class extends Error {
  code;
  status;
  details;
  constructor(code, message, status, details) {
    super(message);
    this.name = "LootApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
};
async function throwFromResponse(res) {
  let code = "internal";
  let message = `request failed with status ${res.status}`;
  let details;
  try {
    const body = await res.json();
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
    details = body.error?.details;
  } catch {
  }
  throw new LootApiError(code, message, res.status, details);
}

// src/currency.ts
var DEFAULT_CURRENCY = {
  name: "credit",
  namePlural: "credits",
  symbol: "$",
  multiplier: 100,
  iconSvg: ""
};
var USD = {
  name: "dollar",
  namePlural: "dollars",
  symbol: "$",
  multiplier: 100,
  iconSvg: ""
};
function currencyFractionDigits(multiplier) {
  switch (multiplier) {
    case 1:
      return 0;
    case 10:
      return 1;
    case 100:
      return 2;
    case 1e3:
      return 3;
  }
}
var NUMBER_FORMATS = [0, 1, 2, 3].map(
  (digits) => new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })
);
function formatAmount(cents, currency, opts = {}) {
  if (!Number.isFinite(cents)) {
    throw new RangeError(`formatAmount: amount is not a finite number of cents: ${cents}`);
  }
  const digits = currencyFractionDigits(currency.multiplier);
  const value = Math.abs(cents) / currency.multiplier;
  const sign = cents < 0 ? "-" : "";
  const body = opts.compact && value >= 1e3 ? `${(value / 1e3).toFixed(1)}k` : NUMBER_FORMATS[digits].format(value);
  return `${sign}${currency.symbol}${body}`;
}
function currencyUnitName(cents, currency) {
  return cents === currency.multiplier ? currency.name : currency.namePlural;
}
function formatAmountWithUnit(cents, currency) {
  const amount = formatAmount(cents, currency);
  return currency.symbol === "" ? `${amount} ${currencyUnitName(cents, currency)}` : amount;
}

// src/index.ts
var DEFAULT_BASE_URL = "https://api.lootos.dev";
var LootClient = class {
  config;
  sessionToken;
  fetchImpl;
  baseUrl;
  constructor(config) {
    this.config = config;
    this.sessionToken = config.sessionToken ?? null;
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
    this.baseUrl = (config.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, "");
  }
  setSessionToken(token) {
    this.sessionToken = token;
  }
  getSessionToken() {
    return this.sessionToken;
  }
  // ---- boxes (publishable key) --------------------------------------------
  /**
   * The boxes your site offers. Forge boxes (shopper-built, `COMMUNITY`) are left out unless
   * `type: "COMMUNITY"` asks for them alone.
   */
  async listBoxes(opts = {}) {
    const query = opts.type ? `?type=${encodeURIComponent(opts.type)}` : "";
    const body = await this.request("GET", `/v1/boxes${query}`);
    return body.data;
  }
  async getBox(boxId) {
    const body = await this.request(
      "GET",
      `/v1/boxes/${encodeURIComponent(boxId)}`
    );
    return body.box;
  }
  // ---- end-user session surface ---------------------------------------------
  async getMe() {
    const body = await this.request("GET", "/v1/me", { session: true });
    return body.user;
  }
  /** The user's balance in cents plus the environment's display currency (see `formatAmount`). */
  async getBalance() {
    return this.request("GET", "/v1/me/balance", { session: true });
  }
  async listMyOpens(params) {
    const query = new URLSearchParams();
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.size ? `?${query}` : "";
    return this.request("GET", `/v1/me/opens${qs}`, { session: true });
  }
  /**
   * Opens a box: reveals items without debiting the balance. Purchase them with
   * claimReveal (or turn on auto-claim via updateMySettings). Requires a
   * session token. Retries are safe: an Idempotency-Key is generated per
   * logical open.
   */
  async openBox(args) {
    const idempotencyKey = args.idempotencyKey ?? crypto.randomUUID();
    return this.request("POST", `/v1/boxes/${encodeURIComponent(args.boxId)}/opens`, {
      session: true,
      headers: { "idempotency-key": idempotencyKey },
      body: {
        clientSeed: args.clientSeed,
        amount: args.amount,
        expectedVersion: args.expectedVersion
      }
    });
  }
  /**
   * Purchases the revealed items of an open group at their disclosed price.
   * Idempotent per reveal: a repeat claim replays the original result.
   */
  async claimReveal(args) {
    return this.request(
      "POST",
      `/v1/reveals/${encodeURIComponent(args.openGroupId)}/claim`,
      {
        session: true,
        body: { expectedPurchaseAmount: args.expectedPurchaseAmount }
      }
    );
  }
  /**
   * Purchases the revealed items at their disclosed price and, in the same transaction, sells
   * every one back to the house for its value: the balance moves by Σ value − price and the
   * ledger shows a `claim_debit` then a `sell_credit`. Idempotent per reveal; a reveal already
   * claimed as owned items answers `reveal_already_claimed`.
   */
  async sellBackReveal(args) {
    return this.request(
      "POST",
      `/v1/reveals/${encodeURIComponent(args.openGroupId)}/sell-back`,
      {
        session: true,
        body: { expectedPurchaseAmount: args.expectedPurchaseAmount }
      }
    );
  }
  /**
   * The user's inventory, newest first: the units they own after a claim. `status` narrows to
   * one of them — `owned` is what can be sold back or withdrawn.
   */
  async listMyInventory(params) {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.size ? `?${query}` : "";
    return this.request("GET", `/v1/me/inventory${qs}`, {
      session: true
    });
  }
  /**
   * Sells owned units back to the house for Σ their value, crediting the balance in one
   * transaction. All or nothing: a unit that cannot be sold — already sold, being shipped, or
   * worth nothing — refuses the sale with `inventory_item_not_sellable` and names it in
   * `details.inventoryItemId` and `details.reason`.
   */
  async sellInventoryItems(args) {
    const body = await this.request(
      "POST",
      "/v1/me/inventory/sell",
      { session: true, body: { inventoryItemIds: args.inventoryItemIds } }
    );
    return body.sale;
  }
  /**
   * Sells one owned unit back to the house for its value: the sale above with a single id, for
   * an inventory tile that sells the unit it sits on. Refused the same way, with
   * `inventory_item_not_sellable` naming the unit and the reason.
   */
  async sellInventoryItem(args) {
    const body = await this.request(
      "POST",
      `/v1/me/inventory/${encodeURIComponent(args.inventoryItemId)}/sell`,
      { session: true }
    );
    return body.sale;
  }
  /**
   * Redeems a gift code into the user's balance as one `gift_code` ledger entry; dashes, spaces
   * and case in `code` are ignored. Rejects with `gift_code_not_redeemable`, whose
   * `details.reason` says why (`unknown`, `redeemed`, `expired` or `voided`), and with
   * `account_locked` on a locked account.
   */
  async redeemGiftCode(code) {
    return this.request("POST", "/v1/me/gift-codes/redeem", {
      session: true,
      body: { code }
    });
  }
  /**
   * The user's notifications, newest first, with the unread count beside them. `unread: true`
   * lists only unread ones; pass `nextCursor` back as `cursor` for the next page.
   */
  async listNotifications(params) {
    const query = new URLSearchParams();
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.unread) query.set("unread", "true");
    const qs = query.size ? `?${query}` : "";
    return this.request("GET", `/v1/me/notifications${qs}`, {
      session: true
    });
  }
  /** The unread count alone: a bell's badge. */
  async getUnreadNotificationCount() {
    const body = await this.request(
      "GET",
      "/v1/me/notifications/unread-count",
      { session: true }
    );
    return body.unreadCount;
  }
  /** Marks the named notifications, or every unread one (`{ all: true }`), read. */
  async markNotificationsRead(args) {
    return this.request("POST", "/v1/me/notifications/read", {
      session: true,
      body: args
    });
  }
  /** The categories the environment sends, each with whether the user receives it. */
  async getNotificationSettings() {
    return this.request("GET", "/v1/me/notification-settings", {
      session: true
    });
  }
  /**
   * Replaces the user's opt-outs, whole: a category left out is turned back on. Naming a required
   * category (`orders`, `balance`, `account`) rejects with `invalid_request`.
   */
  async updateNotificationSettings(args) {
    return this.request("PUT", "/v1/me/notification-settings", {
      session: true,
      body: args
    });
  }
  /** The user's outstanding unclaimed reveal, if any (at most one exists). */
  async getUnclaimedReveal() {
    const body = await this.request(
      "GET",
      "/v1/me/reveals/unclaimed",
      { session: true }
    );
    return body.reveal;
  }
  async updateMySettings(args) {
    const body = await this.request("PUT", "/v1/me/settings", {
      session: true,
      body: args
    });
    return body.user;
  }
  /**
   * Sets or changes the user's public username: 3–20 letters, digits or underscores, unique per
   * environment regardless of case. Rejects with `username_taken` when another user holds it and
   * `invalid_request` when the name breaks a rule (the message names it).
   */
  async updateMyUsername(username) {
    const body = await this.request("PUT", "/v1/me/username", {
      session: true,
      body: { username }
    });
    return body.user;
  }
  /**
   * Starts (or resumes) identity verification for the current user where the site requires it.
   * Open the returned `url` in a new tab; the outcome arrives later on `getMe().kycStatus` /
   * `kycVerified`. Rejects with `invalid_request` when verification is not required here or a
   * decision already stands.
   */
  async startKycSession(args) {
    return this.request("POST", "/v1/me/kyc/session", {
      session: true,
      body: args
    });
  }
  // ---- profiles (any key or session) ---------------------------------------------
  /**
   * An end user as other users may see them: display name, member-since and counts. `endUserId`
   * is what a battle seat's `endUser.id` carries. Rejects with `not_found` for an id outside this
   * key's environment.
   */
  async getProfile(endUserId) {
    const body = await this.request(
      "GET",
      `/v1/profiles/${encodeURIComponent(endUserId)}`
    );
    return body.profile;
  }
  // ---- fairness (public) --------------------------------------------------------
  async getSeed(date) {
    return this.request("GET", `/v1/fairness/seeds/${encodeURIComponent(date)}`);
  }
  async getOpenVerification(openId) {
    return this.request(
      "GET",
      `/v1/opens/${encodeURIComponent(openId)}/verification`
    );
  }
  // ---- internals -------------------------------------------------------------------
  async request(method, path, opts = {}) {
    const headers = {
      "x-publishable-key": this.config.publishableKey,
      ...opts.headers
    };
    if (opts.session) {
      if (!this.sessionToken) {
        throw new LootApiError("unauthorized", "no session token set", 401);
      }
      headers.authorization = `Bearer ${this.sessionToken}`;
    }
    if (opts.body !== void 0) headers["content-type"] = "application/json";
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: opts.body !== void 0 ? JSON.stringify(opts.body) : void 0
    });
    if (res.ok) return await res.json();
    if (res.status === 401 && opts.session && !opts.isRetry && this.config.onSessionExpired) {
      const fresh = await this.config.onSessionExpired();
      if (fresh) {
        this.sessionToken = fresh;
        return this.request(method, path, { ...opts, isRetry: true });
      }
    }
    return throwFromResponse(res);
  }
};

export {
  LootApiError,
  throwFromResponse,
  DEFAULT_CURRENCY,
  USD,
  currencyFractionDigits,
  formatAmount,
  currencyUnitName,
  formatAmountWithUnit,
  DEFAULT_BASE_URL,
  LootClient
};
//# sourceMappingURL=chunk-OQCUF2B7.js.map